import { weatherService } from '../services/weatherService';

// Map weatherService.getWindDirection(deg) => cardinal string
export function getCardinalFromDegrees(degrees) {
  return weatherService.getWindDirection(degrees || 0);
}

// Compare stored optimal wind(s) to current wind degrees
// - Single: optimalCode 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | ''
// - Multi: optimalCodes array of above (excluding '')
export function isWindOptimal(optimalCode, degrees) {
  if (!optimalCode || optimalCode === '') return true; // No preference → always optimal
  const current = getCardinalFromDegrees(degrees);
  return current === optimalCode;
}

export function isWindInAnyOptimal(degrees, optimalCodes) {
  if (!Array.isArray(optimalCodes)) return isWindOptimal(optimalCodes || '', degrees);
  if (optimalCodes.length === 0) return true; // treat empty as no preference
  const current = getCardinalFromDegrees(degrees);
  return optimalCodes.includes(current);
}

export const EIGHT_DIRECTIONS = ['N','NE','E','SE','S','SW','W','NW'];

export function degreesToEightCardinal(deg) {
  const d = ((deg % 360) + 360) % 360;
  const idx = Math.round(d / 45) % 8;
  return EIGHT_DIRECTIONS[idx];
}

/** Normalize wind prop from BaseMap (number or { degrees }). */
export function resolveWindDegrees(currentWindDegrees) {
  if (currentWindDegrees == null && currentWindDegrees !== 0) return null;
  if (typeof currentWindDegrees === 'number') return currentWindDegrees;
  if (typeof currentWindDegrees === 'object' && typeof currentWindDegrees.degrees === 'number') {
    return currentWindDegrees.degrees;
  }
  return null;
}

/**
 * Stand highlight on map: no preference → not optimal (no green halo).
 * Matches legacy BaseMap isOptimalWind behavior.
 */
export function isStandOptimalForWind(feature, currentWindDegrees) {
  const deg = resolveWindDegrees(currentWindDegrees);
  if (deg == null) return false;
  const current = degreesToEightCardinal(deg);
  const arr = getOptimalWindsArray(feature);
  if (arr.length > 0) return arr.includes(current);
  const optimal = feature?.optimal_wind;
  if (!optimal || optimal === '') return false;
  return current === optimal;
}

function asWindList(value) {
  if (Array.isArray(value)) {
    return value.map((w) => String(w || '').trim()).filter(Boolean);
  }
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map((w) => String(w || '').trim()).filter(Boolean);
    }
    if (typeof parsed === 'string' && parsed.trim()) return [parsed.trim()];
  } catch (_) {
    /* Mapbox often leaves arrays as "NW,W" rather than JSON */
  }
  return trimmed.split(/[,\s]+/).filter(Boolean);
}

/**
 * Optimal winds from a stand/feature, including nested Mapbox click payloads.
 * Empty optimal_winds falls back to the singular optimal_wind field.
 */
export function getOptimalWindsArray(feature) {
  try {
    const lists = [
      feature?.optimal_winds,
      feature?.optimalWinds,
      feature?.properties?.optimal_winds,
      feature?.properties?.optimalWinds,
      feature?.location?.optimal_winds,
    ];
    for (const candidate of lists) {
      const arr = asWindList(candidate);
      if (arr.length > 0) return arr;
    }
    const singles = [
      feature?.optimal_wind,
      feature?.optimalWind,
      feature?.properties?.optimal_wind,
      feature?.properties?.optimalWind,
      feature?.location?.optimal_wind,
    ];
    for (const code of singles) {
      if (code && code !== '') return [String(code)];
    }
    return [];
  } catch (_) {
    return [];
  }
}

export function computePointWindFields(feature, currentWindDegrees) {
  const deg = resolveWindDegrees(currentWindDegrees);
  if (deg == null) {
    return { isOptimalWind: false, current_wind_cardinal: '', current_wind_degrees: null };
  }
  const cardinal = degreesToEightCardinal(deg);
  return {
    isOptimalWind: isStandOptimalForWind(feature, currentWindDegrees),
    current_wind_cardinal: cardinal,
    current_wind_degrees: deg
  };
}


